import {useSiteKeywords} from "~/composables/useSiteKeywords"
import {useUniWebTemplate} from "~/composables/useUniWebTemplate"
import {TemplateList} from "~/libs/constants/TemplateList"
import type CncAdPosition from "~/libs/interfaces/CncAdPosition"
import {CncAdsConfig, CncAdsPageTypeConfig} from "~/config/CncAdsConfig"
import {CncAdsTemplate} from "~/libs/constants/CncAdsTemplate"
import {CncAdsForceArea} from "~/libs/constants/CncAdsForceArea"
import type CncAdPageProps from "~/libs/interfaces/CncAdPageProps"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {CncAdsWebType} from "~/libs/constants/CncAdsWebType"
import type {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"

declare global {
  interface Window {
    __cncPageDefinition?: any
    __cncAdsNewAdSlots?: any
    __cncAdsInitialized?: any
    __cncAdsQueue?: any
    __cncPageEvent?: any
  }
}

export const CNC_PAGE_PROPS_ALLOWED_KEYS = ['template', 'webType', 'forceArea', 'responsiveBreakpoint', 'keywords']

export const useCncAdsWrapper = () => {
  const logger = useFrontendLogger()

  /**
   * Init CncAddWrapper
   */
  const init = () => {
    const runtimeConfig = useRuntimeConfig()
    const cncWrapperConfig = runtimeConfig.public.cncWrapper
    const adsEnabled = runtimeConfig.public.adsEnabled
    if (adsEnabled && cncWrapperConfig?.enabled) {
      logger?.info('CncAdsWrapper ENABLED')
      if (!cncWrapperConfig.site) {
        logger?.warn(`CncAdsWrapper ENABLED but 'config.public.cncWrapper.site' missing`)
        return
      }

      if (!cncWrapperConfig.scripts) {
        logger?.warn(`CncAdsWrapper ENABLED but 'config.public.cncWrapper.scripts' missing`)
        return
      }

      if (!cncWrapperConfig.links) {
        logger?.warn(`CncAdsWrapper ENABLED but 'config.public.cncWrapper.links' missing`)
        return
      }

      const keywords = useSiteKeywords()

      const script = []
      for (let i in cncWrapperConfig.scripts) {
        const scriptItem: Record<string, any> = cncWrapperConfig.scripts[i]
        script.push({
          src: scriptItem.src,
          id: scriptItem.id,
          type: "application/javascript",
          async: scriptItem.async,
          fetchpriority: scriptItem.fetchpriority,
          referrerpolicy: scriptItem.referrerpolicy,
        })
      }

      const link = []
      for (let i in cncWrapperConfig.links) {
        const linkItem: Record<string, any> = cncWrapperConfig.links[i]
        link.push({
          href: linkItem.href,
          id: linkItem.id,
          rel: linkItem.rel,
        })
      }

      if (import.meta.client && window) {
        window.__cncPageDefinition = {
          "template": cncWrapperConfig.template || CncAdsTemplate.CATEGORY,
          "webType": cncWrapperConfig.webType || CncAdsWebType.RESPONSIVE,
          "browserEngine": cncWrapperConfig.browserEngine || 'SPA',
          "keywords": keywords || [],
          "site": cncWrapperConfig.site,
          "responsiveBreakpoint": Number(cncWrapperConfig.responsiveBreakpoint || 1080)
        }
        useHead({
          script,
          link
        })
      } else {
        useHead({
          link
        })
      }
    }
  }

  /**
   * Get positions config for template
   */
  const getConfig = (): CncAdPosition[] => {
    return useUniWebTemplate().getTemplateListItem() !== null ? CncAdsConfig[useUniWebTemplate().getTemplateListItem() as TemplateList] : []
  }

  /**
   * Get position config for given position and page
   * @param position
   * @param page
   */
  const getPositionsConfig = (position: UniFeAdsPosition, page: CncAdsPage): CncAdPosition[] => {
    const config = getConfig()

    return config.filter((positionConfig) => {
      return (
        positionConfig.uniFePosition === position &&
        (positionConfig.page.includes(CncAdsPage.ALL) || positionConfig.page.includes(page)) &&
        (!positionConfig.disabledPage || !positionConfig.disabledPage.includes(page))
      )
    })
  }

  /**
   * Get page type props
   * @param page
   */
  const getPageTypeProps = (page: CncAdsPage): CncAdPageProps | undefined => {
    const pageTypesConfigs = useUniWebTemplate().getTemplateListItem() !== null ? CncAdsPageTypeConfig[useUniWebTemplate().getTemplateListItem() as TemplateList] : [] as Partial<Record<CncAdsPage, CncAdPageProps>>
    if (pageTypesConfigs && pageTypesConfigs[page]) {
      return pageTypesConfigs[page]
    }
    return {
      template: CncAdsTemplate.CATEGORY,
      forceArea: CncAdsForceArea.OSTATNI,
    }
  }

  /**
   * Change Cnc page props
   */

  const changePageAds = (page: CncAdsPage, props: any = {}) => {
    logger?.info('CncAdsWrapper changePageAds', {
      page,
      props
    })

    const runtimeConfig = useRuntimeConfig()
    const cncWrapperConfig = runtimeConfig.public.cncWrapper

    const pageTypeProps = getPageTypeProps(page)

    if (!cncWrapperConfig?.enabled || !import.meta.client || !window || !pageTypeProps || typeof pageTypeProps !== 'object') {
      return
    }

    const pageProps = {...pageTypeProps, ...props}

    const pageDefinition = window.__cncPageDefinition || {};

    CNC_PAGE_PROPS_ALLOWED_KEYS.forEach(key => {
      if (pageProps[key] !== undefined) {
        pageDefinition[key] = pageProps[key];
      }
    })

    window.__cncPageDefinition = pageDefinition;

    const initializeAdSlots = () => {
      if (typeof window.__cncAdsNewAdSlots === 'function') {
        window.__cncAdsNewAdSlots();
      }
    };

    window.__cncAdsInitialized
      ? initializeAdSlots()
      : (window.__cncAdsQueue = window.__cncAdsQueue || []).push(initializeAdSlots);
  }

  const pageEvent = (event: CncAdsTemplate) => {
    if (window?.__cncPageEvent) {
      logger?.info('CncAdsWrapper pageEvent called', {
        event
      })
      window?.__cncPageEvent(event)
    }
  }

  return {
    init,
    changePageAds,
    getPositionsConfig,
    pageEvent,
  }
}


